import { Text } from "@hackthenorth/north";
import { Button as ButtonNew, Card, Spinner, TextInput } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useClipboard } from "use-clipboard-copy";

import { Header, Icon, PageWrapper } from "src/shared/components";
import { errorToast, successToast } from "src/shared/components/Toast";
import { EVENT_ID } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts";
import { useGetMeQuery } from "src/shared/contexts/UserContext/graphql/getMe.generated";
import { useUpdateMyProfileMutation } from "src/shared/contexts/UserContext/graphql/updateMyProfile.generated";

import DeleteAccountModal from "./DeleteAccountModal";

const AccountManagement = () => {
  const { info } = useUserContext();
  const clipboard = useClipboard();
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [legalNameTouched, setLegalNameTouched] = useState(false);

  const { data, loading, refetch } = useGetMeQuery({
    variables: { eventId: EVENT_ID },
  });
  const [updateMyProfile, { loading: isSaving }] = useUpdateMyProfileMutation();

  const me = data?.me;

  useEffect(() => {
    if (me) {
      setName(me.name ?? "");
      setLegalName(me.legal_name ?? "");
    }
  }, [me]);

  const isEmailVerified = !!me?.email_verified_at;

  const trimmedName = name.trim();
  const originalName = (me?.name ?? "").trim();
  const trimmedLegalName = legalName.trim();
  const originalLegalName = (me?.legal_name ?? "").trim();

  const isDirty =
    trimmedName !== originalName ||
    (legalNameTouched && trimmedLegalName !== originalLegalName);

  const isNameValid = name.trim().length > 0;

  const resetForm = () => {
    setName(me?.name ?? "");
    setLegalName(me?.legal_name ?? "");
    setLegalNameTouched(false);
  };

  const onCancel = () => {
    resetForm();
    setIsEditing(false);
  };

  const onSave = async () => {
    if (!isDirty || !isNameValid) return;

    const legalNameValue = legalNameTouched
      ? trimmedLegalName
      : me?.legal_name ?? "";

    try {
      const { errors } = await updateMyProfile({
        variables: {
          name: trimmedName,
          legal_name: legalNameValue,
        },
      });

      if (errors?.length) {
        throw errors[0];
      }

      await refetch({ eventId: EVENT_ID });
      successToast("Profile updated successfully.");
      setIsEditing(false);
      setLegalNameTouched(false);
    } catch (e) {
      const message =
        e instanceof Error
          ? e.message
          : "We weren't able to update your profile, please try again.";
      errorToast(message);
    }
  };

  const onCopyEmail = () => {
    if (!me?.email) return;
    clipboard.copy(me.email);
    successToast("Copied email to clipboard!");
  };

  return (
    <>
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        userEmail={info?.email ?? ""}
      />
      <Header
        title="Account Management"
        subtitle="Manage your account details and deletion requests."
      />
      <AccountWrapper>
        <ProfileWrapper>
          <ProfileHeaderWrapper>
            <ProfileHeader>
              <Text mods="heading h3 bold">Profile information</Text>
            </ProfileHeader>
          </ProfileHeaderWrapper>
          {loading ? (
            <Spinner />
          ) : (
            <>
              <InputRow>
                <HalfCol>
                  <Text mods="heading h4 bold">Display Name</Text>
                  {isEditing ? (
                    <FullInput
                      size="md"
                      placeholder="Enter your display name"
                      error={
                        !isNameValid
                          ? "This field cannot be left empty"
                          : undefined
                      }
                      value={name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setName(e.target.value);
                      }}
                    />
                  ) : (
                    <FieldValue>{me?.name}</FieldValue>
                  )}
                  <FieldDescription mods="caption grey">
                    Your preferred name for use across Hack the North. Example:
                    Goosey
                  </FieldDescription>
                </HalfCol>
                <HalfCol>
                  <LabelRow>
                    <Text mods="heading h4 bold">Legal Name</Text>
                    {!isEditing && (
                      <ButtonNew
                        color="tertiary"
                        size="md"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit <Icon name="edit" size="h3" />
                      </ButtonNew>
                    )}
                  </LabelRow>
                  {isEditing ? (
                    <FullInput
                      size="md"
                      placeholder="Enter your legal name"
                      value={legalName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setLegalNameTouched(true);
                        setLegalName(e.target.value);
                      }}
                    />
                  ) : (
                    <FieldValue>{me?.legal_name || "—"}</FieldValue>
                  )}
                  <FieldDescription mods="caption grey">
                    Your legal name as it appears on your ID. Example: Sir Goose
                    Reginald the 3rd
                  </FieldDescription>
                </HalfCol>
              </InputRow>
              {isEditing && (
                <ButtonWrapper>
                  <ButtonNew
                    color="secondary"
                    size="lg"
                    onClick={onCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </ButtonNew>
                  <ButtonNew
                    color="primary"
                    size="lg"
                    onClick={onSave}
                    disabled={!isDirty || !isNameValid || isSaving}
                  >
                    Save
                  </ButtonNew>
                </ButtonWrapper>
              )}
              <Row>
                <Text mods="heading h4 bold">Email</Text>
                <EmailRow>
                  <Text>{me?.email}</Text>
                  <CopyButton
                    type="button"
                    aria-label="Copy email"
                    onClick={onCopyEmail}
                  >
                    <Icon name="copy" size="h3" />
                  </CopyButton>
                  <VerifiedBadge $verified={isEmailVerified}>
                    {isEmailVerified ? "✓ Verified" : "Not verified"}
                  </VerifiedBadge>
                </EmailRow>
                <Text mods="caption grey">
                  Need to change this? Email hello@hackthenorth.com
                </Text>
              </Row>
            </>
          )}
        </ProfileWrapper>
        <SectionCard>
          <SectionContent>
            <Text mods="heading h3 bold">Delete account</Text>
            <Text>
              Request deletion of your Hack the North account. We&apos;ll
              process your request by email.
            </Text>
            <ButtonNew
              color="secondary"
              size="md"
              onClick={() => setShowDeleteAccountModal(true)}
            >
              Delete account
            </ButtonNew>
          </SectionContent>
        </SectionCard>
      </AccountWrapper>
    </>
  );
};

const AccountWrapper = styled(PageWrapper)`
  max-width: 800px;
`;

const ProfileWrapper = styled.div`
  margin-bottom: 42px;
`;

const ProfileHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InputRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin: 24px 0 36px 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
`;

const Row = styled.div`
  display: block;
  margin: 24px 0 36px 0;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const HalfCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  max-width: 45%;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    margin: 12px 0;
  }
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const FieldValue = styled(Text)`
  display: block;
`;

const FieldDescription = styled(Text)`
  display: block;
`;

const FullInput = styled(TextInput)`
  width: 100%;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;

  &:hover {
    color: #374151;
  }
`;

const VerifiedBadge = styled.span<{ $verified: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $verified }) => ($verified ? "#059669" : "#6b7280")};
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  column-gap: 10px;
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
  padding: 24px !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06) !important;
  border-radius: 8px !important;
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export default AccountManagement;
